import React, {useState, ReactNode, useCallback, useEffect} from 'react';
import MatrixRoomContext from './context';
import * as sdk from 'matrix-js-sdk';
import {useMatrixContext} from '@/contexts/matrix/client';
import {
  checkIfUserIsRoomMember,
  getCurrentRoom,
  loadInitialEvents
} from '@/services/matrix/room';
import {
  Multimedia,
  MultimediaDownloadInfo,
  MultimediaFileInfo,
  MultimediaUploadInfo,
  SYSTEM_MESSAGE_TYPES,
  SystemMessageAction,
  SystemMessageData
} from '@/types/matrix';
import {useMatrixUnreadEventsContext} from '@/contexts/matrix/unread-events';
import {MATRIX_URL_SUFFIX} from '@/services/matrix/client/constants';
import {ResizedImageInfo} from '@/types/matrix';
import {
  deleteImage,
  deleteMultipleMessages,
  deleteSingleMessage,
  downloadMultipleMultimedia,
  downloadSingleMultimedia,
  resizeImageToFile,
  sendImages,
  sendReplyMessage,
  sendSystemMessage,
  sendTextMessage,
  sendVideos,
  setMessageRead
} from '@/services/matrix/messages';
import {createDirectChat} from '@/services/matrix/room/create-direct-chat';
import {useDispatch} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {makeMatrixUserIdFromUserMail} from '@/components/matrix-chat/utils/helper-functions';

type MatrixRoomProviderProps = {
  children: ReactNode;
  childId?: string;
  roomId?: string;
};

export const MatrixRoomProvider: React.FC<MatrixRoomProviderProps> = ({
  children,
  childId,
  roomId
}) => {
  const [currentMatrixRoomId, setCurrentMatrixRoomId] = useState<string | null>(
    null
  );
  const [matrixEventsCount, setMatrixEventsCount] = useState<number>(0);
  const [readyToHandlePagination, setReadyToHandlePagination] =
    useState<boolean>(false);
  const [matrixEventToQuote, setMatrixEventToQuote] =
    useState<sdk.MatrixEvent | null>(null);
  const [activeMatrixEvent, setActiveMatrixEvent] =
    useState<sdk.MatrixEvent | null>(null);

  const [matrixEvents, setMatrixEvents] = useState<sdk.MatrixEvent[]>([]);
  const [loadingOlderMatrixEvents, setLoadingOlderMatrixEvents] =
    useState(false);
  const [hasMoreOlderMatrixEvents, setHasMoreOlderMatrixEvents] =
    useState(true);
  const [lastSentMatrixEvent, setLastSentMatrixEvent] =
    useState<sdk.MatrixEvent | null>(null);

  const [lastEventSentByUser, setLastEventSentByUser] =
    useState<boolean>(false);

  const [redactingEvent, setRedactingEvent] = useState<boolean>(false);

  const [matrixEventsUpdated, setMatrixEventsUpdated] = useState(false);

  const [userDialogOpen, setUserDialogOpen] = useState(false);

  const [currentHoveredEvent, setCurrentHoveredEvent] = useState<string | null>(
    null
  );

  const [multimediaDialogOpen, setMultimediaDialogOpen] = useState(false);

  const [multimediaFiles, setMultimediaFiles] = useState<MultimediaFileInfo[]>(
    []
  );

  const [roomInitRetries, setRoomInitRetries] = useState(0);

  const [isRoomJoined, setIsRoomJoined] = useState(false);

  const [isLoadingRoom, setIsLoadingRoom] = useState(true);

  const [isSendingEvent, setIsSendingEvent] = useState(false);

  const [finishedSendingImages, setFinishedSendingImages] = useState(false);

  const [multimediaEventsToSend, setMultimediaEventsToSend] = useState(0);

  const [multimediaTypeUploaded, setMultimediaTypeUploaded] =
    useState<Multimedia | null>(null);

  const [windowChatOpen, setWindowChatOpen] = useState(false);

  const dispatch = useDispatch();

  const {matrixClient, matrixUserId} = useMatrixContext();

  const {unreadEventsCount} = useMatrixUnreadEventsContext();

  const roomAlias = `#${childId}:${MATRIX_URL_SUFFIX}`;

  const {t} = useTranslation();

  const updateMatrixEventsState = useCallback(
    (events: sdk.MatrixEvent[]) => {
      try {
        const newEvents = [...events];
        setMatrixEvents(newEvents);
        setMatrixEventsUpdated(true);
      } catch (error) {
        console.error('Error updating matrix events: ', error);
      }
    },
    [setMatrixEvents]
  );

  const addMatrixEvent = useCallback((event: sdk.MatrixEvent) => {
    try {
      setMatrixEvents((prevEvents) => {
        if (
          event.getType() !== 'm.room.redaction' &&
          prevEvents.some(
            (e) =>
              e.getEffectiveEvent().event_id ===
              event.getEffectiveEvent().event_id
          )
        ) {
          return [...prevEvents];
        } else {
          return [...prevEvents, event];
        }
      });
    } catch (error) {
      console.error('Error adding matrix event: ', error);
    }
  }, []);

  const updateMatrixEvent = useCallback(
    (updatedEvent: sdk.MatrixEvent) => {
      try {
        setMatrixEvents((prevEvents) =>
          prevEvents
            ? prevEvents.map((event) =>
                event.getEffectiveEvent().event_id ===
                updatedEvent.getEffectiveEvent().event_id
                  ? updatedEvent
                  : event
              )
            : [updatedEvent]
        );
      } catch (error) {
        console.error('Error updating matrix event: ', error);
      }
    },
    [setMatrixEvents]
  );

  const replaceMatrixEvents = useCallback(
    (eventToDeleteId: string, eventToAdd: sdk.MatrixEvent) => {
      try {
        setMatrixEvents((prevEvents) =>
          prevEvents.map((event) =>
            event.getEffectiveEvent().event_id === eventToDeleteId
              ? eventToAdd
              : event
          )
        );
      } catch (error) {
        console.error('Error replacing matrix events: ', error);
      }
    },
    []
  );

  const handleCheckIfUserIsRoomMember = useCallback(
    async (roomId: string) => {
      try {
        if (matrixClient && matrixUserId) {
          const isUserMember = await checkIfUserIsRoomMember(
            matrixClient,
            roomId,
            matrixUserId
          );
          return isUserMember;
        }
      } catch (error) {
        console.error('Cannot check if user is room member: ', error);
        return false;
      }
    },
    [matrixClient, matrixUserId]
  );

  const handleSendSystemMessage = useCallback(
    async (actionType: SystemMessageAction, data: SystemMessageData) => {
      if (matrixClient && currentMatrixRoomId) {
        await sendSystemMessage(
          matrixClient,
          currentMatrixRoomId,
          actionType,
          data
        );
      }
    },
    [currentMatrixRoomId, matrixClient]
  );

  const handleFetchRoomData = useCallback(
    async (roomId: string) => {
      try {
        if (matrixClient) {
          const currentRoom = await getCurrentRoom(matrixClient, roomId);

          if (currentRoom) {
            setCurrentMatrixRoomId(roomId);
            const matrixEvents = await loadInitialEvents(
              matrixClient,
              roomId,
              unreadEventsCount
            );
            updateMatrixEventsState(matrixEvents);
            setMatrixEventsCount(matrixEvents.length);
            setIsLoadingRoom(false);
          }
        }
      } catch (error) {
        console.error("couldn't fetch room data", error);
      }
    },
    [matrixClient, unreadEventsCount, updateMatrixEventsState]
  );

  const initMatrixRoom = useCallback(
    async (
      invitedUserId?: string,
      existingRoomId?: string,
      withClosedWindow?: boolean
    ) => {
      try {
        if (matrixClient && matrixUserId) {
          if (existingRoomId) {
            setIsRoomJoined(true);
            if (!withClosedWindow) {
              setWindowChatOpen(true);
            }

            handleFetchRoomData(existingRoomId);
          }
          if (invitedUserId) {
            const directRoom = await createDirectChat(
              invitedUserId,
              matrixClient
            );
            if (directRoom) {
              setIsRoomJoined(true);
              handleFetchRoomData(directRoom.room_id);
              if (withClosedWindow) {
                return directRoom;
              }
              setWindowChatOpen(true);
            }
          }
          if (!isRoomJoined) {
            let roomIdToUse;

            if (roomId) {
              roomIdToUse = roomId;
            } else {
              const roomData = await matrixClient.getRoomIdForAlias(roomAlias);

              roomIdToUse = roomData.room_id;
            }

            const roomExists = matrixClient.getRoom(roomIdToUse);

            setRoomInitRetries(0);
            const isMember = await handleCheckIfUserIsRoomMember(roomIdToUse);
            if ((!isMember || !roomExists) && roomInitRetries < 10) {
              setTimeout(() => {
                setRoomInitRetries(roomInitRetries + 1);
                console.log(
                  `retry init matrix room with ${roomInitRetries + 1} attempt`
                );
                initMatrixRoom();
              }, 1500);
              return;
            }

            if (isMember) {
              setIsRoomJoined(true);
              handleFetchRoomData(roomIdToUse);
            }
          }
        }
      } catch (error) {
        console.error('Error during initMatrixRoom:', error);
      } finally {
        setRoomInitRetries(0);
      }
    },
    [
      matrixClient,
      matrixUserId,
      isRoomJoined,
      handleFetchRoomData,
      roomId,
      roomAlias,
      roomInitRetries,
      handleCheckIfUserIsRoomMember
    ]
  );

  const loadOlderMatrixEvents = useCallback(async () => {
    if (!hasMoreOlderMatrixEvents) {
      return;
    }

    setLoadingOlderMatrixEvents(true);

    try {
      if (matrixClient && currentMatrixRoomId) {
        const currentRoom = matrixClient.getRoom(currentMatrixRoomId);
        if (currentRoom) {
          const roomWithOlderEvents = await matrixClient.scrollback(
            currentRoom,
            20
          );

          const olderEvents = await roomWithOlderEvents
            .getLiveTimeline()
            .getEvents();

          updateMatrixEventsState(olderEvents);

          if (olderEvents.length - matrixEventsCount < 20) {
            setHasMoreOlderMatrixEvents(false);
          }
          setMatrixEventsCount(olderEvents.length);
        }
      }
    } catch (error) {
      console.error('Failed to load older messages:', error);
      setHasMoreOlderMatrixEvents(false);
    }

    setLoadingOlderMatrixEvents(false);
  }, [
    currentMatrixRoomId,
    matrixEventsCount,
    hasMoreOlderMatrixEvents,
    matrixClient,
    setMatrixEventsCount,
    updateMatrixEventsState
  ]);

  const handleSendMatrixTextMessage = useCallback(
    async (messageText: string, directRoomId?: string) => {
      try {
        if (matrixClient) {
          if (directRoomId) {
            await sendTextMessage(
              matrixClient,
              messageText,
              undefined,
              directRoomId
            );
            return;
          }
          if (currentMatrixRoomId) {
            setIsSendingEvent(true);
            if (matrixEventToQuote && currentMatrixRoomId) {
              await sendReplyMessage(
                matrixClient,
                currentMatrixRoomId,
                matrixEventToQuote,
                messageText
              );
              setMatrixEventToQuote(null);

              return;
            }
            await sendTextMessage(
              matrixClient,
              messageText,
              currentMatrixRoomId || undefined,
              undefined
            );
            setLastEventSentByUser(true);
          }
        }
      } catch (error) {
        console.error('Error sending text message: ', error);
      } finally {
        setIsSendingEvent(false);
      }
    },
    [currentMatrixRoomId, matrixClient, matrixEventToQuote]
  );

  const handleDeleteMatrixEvent = useCallback(
    async (
      eventToDelete?: sdk.MatrixEvent,
      eventsToDelete?: sdk.MatrixEvent[]
    ) => {
      if (matrixClient) {
        if (eventToDelete) {
          const deletedEvent = await deleteSingleMessage(
            matrixClient,
            eventToDelete
          );

          if (deletedEvent) {
            updateMatrixEvent(deletedEvent);
          }
        } else if (eventsToDelete) {
          const deletedEvents = await deleteMultipleMessages(
            matrixClient,
            eventsToDelete
          );
          if (deletedEvents) {
            for (const event of deletedEvents) {
              updateMatrixEvent(event);
            }
          }
        }
      }
    },
    [matrixClient, updateMatrixEvent]
  );

  const handleDeleteImage = useCallback(
    async (parentImage: sdk.MatrixEvent, imageToDeleteUrl: string) => {
      try {
        if (matrixClient && currentMatrixRoomId) {
          await deleteImage(
            matrixClient,
            currentMatrixRoomId,
            parentImage,
            imageToDeleteUrl
          );
        }
      } catch (error) {
        console.error('Error deleting image: ', error);
      }
    },
    [currentMatrixRoomId, matrixClient]
  );

  const handleListScrolledToNewestEvent = useCallback(() => {
    if (matrixClient && currentMatrixRoomId) {
      setLastEventSentByUser(false);
    }
  }, [currentMatrixRoomId, matrixClient]);

  const handleResetMatrixEventsUpdatedState = useCallback(() => {
    if (matrixClient && currentMatrixRoomId) {
      setMatrixEventsUpdated(false);
    }
  }, [currentMatrixRoomId, matrixClient]);

  const handleOpenUserDialog = useCallback(() => {
    if (matrixClient && currentMatrixRoomId) {
      setUserDialogOpen(true);
    }
  }, [currentMatrixRoomId, matrixClient]);

  const handleCloseUserDialog = useCallback(() => {
    if (matrixClient && currentMatrixRoomId) {
      setUserDialogOpen(false);
    }
  }, [currentMatrixRoomId, matrixClient]);

  const handleMouseEnterEvent = useCallback((eventId: string) => {
    setCurrentHoveredEvent(eventId);
  }, []);

  const handleMouseLeaveEvent = useCallback(() => {
    setCurrentHoveredEvent(null);
  }, []);

  const handleCloseWindowChat = useCallback(() => {
    setWindowChatOpen(false);
  }, []);

  const handleSetAllEventsRead = useCallback(() => {
    try {
      if (
        matrixClient &&
        currentMatrixRoomId &&
        matrixUserId &&
        matrixEvents.length > 0
      ) {
        const lastEvent = matrixEvents[matrixEvents.length - 1];

        // TODO: Fix error while joining and leaving room

        if (lastEvent.getEffectiveEvent().sender !== matrixUserId) {
          setMessageRead(matrixClient, lastEvent);
        }
        // setMessageRead(matrixClient, lastEvent);
      }
    } catch (error) {
      console.error('Error setting events as read: ', error);
    }
  }, [currentMatrixRoomId, matrixClient, matrixEvents, matrixUserId]);

  const handleSetEventRead = useCallback(
    async (eventToSetRead: sdk.MatrixEvent) => {
      try {
        if (matrixClient && currentMatrixRoomId && matrixUserId) {
          await setMessageRead(matrixClient, eventToSetRead);
        }
      } catch (error) {
        console.error('Failed to set event read: ', error);
      }
    },
    [currentMatrixRoomId, matrixClient, matrixUserId]
  );

  const handleUploadMultimediaFiles = useCallback(
    async (filesToUpload: MultimediaUploadInfo[]) => {
      try {
        const newFilesInfo: MultimediaFileInfo[] = [];

        for (const file of filesToUpload) {
          const previewUrl = URL.createObjectURL(file.file);
          let fileInfo: MultimediaFileInfo;

          if (file.type === 'image') {
            const resizedFile = await resizeImageToFile(
              file.file,
              768,
              1024,
              500 * 1024
            );
            fileInfo = {
              type: 'image',
              file: resizedFile,
              previewUrl: previewUrl
            };
          } else {
            fileInfo = {
              type: 'video',
              file: file.file,
              previewUrl: previewUrl
            };
          }

          newFilesInfo.push(fileInfo);
        }

        setMultimediaFiles((prevFiles) => [...prevFiles, ...newFilesInfo]);
      } catch (error) {
        console.error('Failed to upload files: ', error);
      }
    },
    []
  );

  const handleSelectMultimediaTypeUploaded = useCallback(
    (multimediaType: Multimedia) => {
      if (multimediaType) {
        setMultimediaTypeUploaded(multimediaType);
      }
    },
    []
  );

  const handleCancelMultimediaFileUpload = (index: number) => {
    setMultimediaFiles((currentFiles) =>
      currentFiles.filter((_, i) => i !== index)
    );
  };

  const checkIfIsResizedImageInfo = useCallback(
    (obj: unknown): obj is ResizedImageInfo => {
      if (typeof obj === 'object' && obj !== null) {
        return 'file' in obj && 'width' in obj && 'height' in obj;
      }
      return false;
    },
    []
  );

  const handleSendMultimediaFiles = useCallback(
    async (multimediaFiles: MultimediaFileInfo[], text: string) => {
      try {
        if (matrixClient && currentMatrixRoomId) {
          setIsSendingEvent(true);
          setMultimediaEventsToSend(multimediaFiles.length);

          const imagesToSend = multimediaFiles
            .filter((file) => file.type === 'image')
            .map((file) => {
              if (checkIfIsResizedImageInfo(file.file)) {
                return file.file;
              } else {
                throw new Error('File is not of type ResizedImageInfo');
              }
            });

          const videosToSend = multimediaFiles
            .filter((file) => file.type === 'video')
            .map((file) => {
              return file.file instanceof File ? file.file : file.file.file;
            });

          if (imagesToSend.length > 0) {
            await sendImages(
              matrixClient,
              currentMatrixRoomId,
              imagesToSend,
              text
            );
          }

          if (videosToSend.length > 0) {
            await sendVideos(
              matrixClient,
              currentMatrixRoomId,
              videosToSend,
              text
            );
          }

          setMultimediaFiles([]);
          setMultimediaEventsToSend(0);
          setIsSendingEvent(false);
        }
      } catch (error) {
        console.error('Failed to send image ', error);
      } finally {
        setIsSendingEvent(false);
      }
    },
    [checkIfIsResizedImageInfo, currentMatrixRoomId, matrixClient]
  );

  const handleOpenMultimediaDialog = useCallback(() => {
    setMultimediaDialogOpen(true);
  }, []);

  const handleCloseMultimediaDialog = useCallback(() => {
    setMultimediaDialogOpen(false);
  }, []);

  const handleDownloadSingleMultimedia = useCallback(
    (multimediaInfo: MultimediaDownloadInfo) => {
      try {
        downloadSingleMultimedia(multimediaInfo);
      } catch (error) {
        console.error('Failed to download image: ', error);
      }
    },
    []
  );

  const handleDownloadMultipleMultimedia = useCallback(
    (multimediaInfo: MultimediaDownloadInfo[]) => {
      try {
        downloadMultipleMultimedia(multimediaInfo);
      } catch (error) {
        console.error('Failed to download images: ', error);
      }
    },
    []
  );

  const handleSendDirectMessage = useCallback(
    async (message: string, invitedUserMail: string) => {
      try {
        if (matrixClient) {
          const matrixUserId = makeMatrixUserIdFromUserMail(invitedUserMail);
          const result = await createDirectChat(matrixUserId, matrixClient);
          if (result && result.room_id) {
            await handleSendMatrixTextMessage(message, result.room_id);
          }
        }
      } catch (error) {
        console.error(error);
      }
    },
    [handleSendMatrixTextMessage, matrixClient]
  );

  const handleFinishedSendingImages = useCallback(() => {
    setFinishedSendingImages(true);
  }, []);

  const handleResetSendingImagesState = useCallback(() => {
    setFinishedSendingImages(false);
  }, []);

  useEffect(() => {
    setRoomInitRetries(0);
  }, [roomAlias, matrixUserId]);

  useEffect(() => {
    if (hasMoreOlderMatrixEvents && !loadingOlderMatrixEvents) {
      setReadyToHandlePagination(true);
    } else {
      setReadyToHandlePagination(false);
    }
  }, [hasMoreOlderMatrixEvents, loadingOlderMatrixEvents, matrixEvents]);

  useEffect(() => {
    if (!matrixClient || !currentMatrixRoomId) {
      return;
    }

    const subscribeToRoomEvents = (roomId: string) => {
      const handleTimelineEvent = (event: sdk.MatrixEvent) => {
        const handleEventAsync = async () => {
          try {
            const EXTENDED_TEXT_MESSAGE_TYPES = [
              'm.room.message',
              ...SYSTEM_MESSAGE_TYPES
            ];

            if (
              event.getType() === 'm.room.member' &&
              event.getEffectiveEvent().room_id
            ) {
              const roomId = event.getEffectiveEvent().room_id;
              if (roomId) {
                const isMember = await handleCheckIfUserIsRoomMember(roomId);
                if (!isMember) {
                  setIsRoomJoined(false);
                  matrixClient.removeListener(
                    sdk.RoomEvent.Timeline,
                    handleTimelineEvent
                  );

                  setTimeout(() => {
                    matrixClient.on(
                      sdk.RoomEvent.Timeline,
                      handleTimelineEvent
                    );
                  }, 100);
                } else if (isMember && !isRoomJoined) {
                  setIsRoomJoined(true);
                  handleFetchRoomData(roomId);
                }
              }
            }

            if (
              EXTENDED_TEXT_MESSAGE_TYPES.includes(event.getType()) &&
              event.getRoomId() === roomId &&
              event.getEffectiveEvent().event_id
            ) {
              addMatrixEvent(event);
              setLastSentMatrixEvent(event);
            } else if (
              event.getType() === 'm.room.redaction' &&
              event.getRoomId() === roomId
            ) {
              if (redactingEvent) {
                return;
              }
              setRedactingEvent(true);
              addMatrixEvent(event);
            }
          } catch (error) {
            console.error('Error handling timeline event:', error);
          } finally {
            setRedactingEvent(false);
          }
        };

        handleEventAsync();
      };

      matrixClient.on(sdk.RoomEvent.Timeline, handleTimelineEvent);

      return () => {
        matrixClient.removeListener(
          sdk.RoomEvent.Timeline,
          handleTimelineEvent
        );
      };
    };

    const unsubscribe = subscribeToRoomEvents(currentMatrixRoomId);

    return () => {
      unsubscribe();
    };
  }, [
    addMatrixEvent,
    currentMatrixRoomId,
    matrixClient,
    replaceMatrixEvents,
    handleSendSystemMessage,
    matrixUserId,
    redactingEvent,
    handleFetchRoomData,
    handleCheckIfUserIsRoomMember,
    isRoomJoined,
    t,
    dispatch
  ]);

  useEffect(() => {
    if (multimediaFiles.length === 0) {
      setMultimediaTypeUploaded(null);
    }
  }, [multimediaFiles]);

  return (
    <MatrixRoomContext.Provider
      value={{
        currentMatrixRoomId,
        matrixEvents,
        updateMatrixEventsState,
        addMatrixEvent,
        updateMatrixEvent,
        initMatrixRoom,
        loadOlderMatrixEvents,
        readyToHandlePagination,
        matrixEventToQuote,
        setMatrixEventToQuote,
        activeMatrixEvent,
        setActiveMatrixEvent,
        handleSendMatrixTextMessage,
        handleDeleteMatrixEvent,
        lastSentMatrixEvent,
        handleSendSystemMessage,
        lastEventSentByUser,
        handleListScrolledToNewestEvent,
        matrixEventsUpdated,
        handleResetMatrixEventsUpdatedState,
        userDialogOpen,
        handleOpenUserDialog,
        handleCloseUserDialog,
        handleMouseEnterEvent,
        handleMouseLeaveEvent,
        currentHoveredEvent,
        handleSetEventRead,
        handleSetAllEventsRead,
        multimediaDialogOpen,
        isRoomJoined,
        isLoadingRoom,
        isSendingEvent,
        handleDownloadSingleMultimedia,
        handleDownloadMultipleMultimedia,
        handleDeleteImage,
        handleFinishedSendingImages,
        handleResetSendingImagesState,
        finishedSendingImages,
        multimediaEventsToSend,
        handleUploadMultimediaFiles,
        handleCancelMultimediaFileUpload,
        multimediaFiles,
        handleSendMultimediaFiles,
        handleCloseMultimediaDialog,
        handleOpenMultimediaDialog,
        multimediaTypeUploaded,
        handleSelectMultimediaTypeUploaded,
        handleCloseWindowChat,
        windowChatOpen,
        handleSendDirectMessage
      }}
    >
      {children}
    </MatrixRoomContext.Provider>
  );
};
